import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { products } from "../config/products";
import "./ProductNis2scan.css";

function ProductNis2scan() {
  const product = products.find((p) => p.slug === "nis2scan");

  if (!product) {
    return (
      <div className="nis2-page">
        <div className="container">
          <h1>Product Not Found</h1>
          <Link to="/products">Back to Products</Link>
        </div>
      </div>
    );
  }

  const scrollToSamples = (e) => {
    e.preventDefault();
    const element = document.getElementById("sample-reports");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const controls = [
    {
      num: 1,
      name: "Network Security",
      article: "21(2)(a)",
      desc: "Open ports, exposed services",
      icon: "🔌",
    },
    {
      num: 2,
      name: "Patch Management",
      article: "21(2)(e)",
      desc: "Updates, auto-patch status",
      icon: "📦",
    },
    {
      num: 3,
      name: "Access Control",
      article: "21(2)(i)",
      desc: "Users, SSH, password policy",
      icon: "🔐",
    },
    {
      num: 4,
      name: "Backup & Recovery",
      article: "21(2)(c)",
      desc: "Backup age, restore testing",
      icon: "💾",
    },
    {
      num: 5,
      name: "Log Management",
      article: "21(2)(f)",
      desc: "Retention, auditd, SIEM",
      icon: "📋",
    },
    {
      num: 6,
      name: "Firewall",
      article: "21(2)(a)",
      desc: "UFW, iptables, nftables",
      icon: "🧱",
    },
    {
      num: 7,
      name: "Cryptography & TLS",
      article: "21(2)(h)",
      desc: "TLS versions, disk encryption",
      icon: "🔒",
    },
    {
      num: 8,
      name: "Multi-Factor Auth",
      article: "21(2)(j)",
      desc: "PAM MFA, fail2ban",
      icon: "🔑",
    },
    {
      num: 9,
      name: "Windows & AD",
      article: "21(2)(a-j)",
      desc: "Updates, BitLocker, RDP",
      icon: "🪟",
    },
  ];

  const beforeFindings = [
    { status: "fail", text: "99 pending security updates (HIGH)" },
    { status: "fail", text: "No log retention policy (HIGH)" },
    { status: "fail", text: "No MFA configured (HIGH)" },
    { status: "fail", text: "No restore test evidence (HIGH)" },
    { status: "fail", text: "No password expiry (HIGH)" },
    { status: "warning", text: "HTTP open without HTTPS (LOW)" },
    { status: "pass", text: "Firewall active (PASS)" },
  ];

  const afterFindings = [
    { status: "pass", text: "0 pending updates (PASS)" },
    { status: "pass", text: "90-day log retention (PASS)" },
    { status: "pass", text: "fail2ban active (PASS)" },
    { status: "pass", text: "Restore log found (PASS)" },
    { status: "pass", text: "Password expiry set (PASS)" },
    { status: "warning", text: "HTTP open without HTTPS (LOW)" },
    { status: "pass", text: "Firewall active (PASS)" },
  ];

  return (
    <>
      <Helmet>
        <title>
          nis2scan — NIS2 Article 21 Compliance Scanner | AxiomLabs Solutions
        </title>
        <meta
          name="description"
          content="Single binary. Zero dependencies. Scans all 9 NIS2 Article 21 controls in parallel and produces an auditor-ready PDF report in under 10 seconds. 100% offline, no cloud, open source."
        />
      </Helmet>
      <div className="nis2-page">
        {/* Section 1: Hero */}
        <section className="nis2-hero">
          <div className="container">
            <Link to="/products" className="nis2-back">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back to Products
            </Link>

            <h1 className="nis2-hero__title">{product.name}</h1>
            <p className="nis2-hero__tagline">{product.tagline}</p>

            <div className="nis2-hero__badges">
              <span className="nis2-badge nis2-badge--opensource">
                ⭐ Open Source
              </span>
              <span className="nis2-badge nis2-badge--platforms">
                {product.platforms.join(" + ")}
              </span>
            </div>

            <p className="nis2-hero__description">
              A single binary that scans your infrastructure against all 9 NIS2
              Article 21 controls and produces an auditor-ready PDF report in
              under 10 seconds. No agents to install, no data leaves your
              network, no subscription.
            </p>

            <div className="nis2-hero__cta">
              <a
                href={product.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="nis2-button nis2-button--primary"
              >
                <span>View on GitHub</span>
                <span className="nis2-button__arrow">→</span>
              </a>
              <a
                href="#sample-reports"
                onClick={scrollToSamples}
                className="nis2-button nis2-button--secondary"
              >
                <span>Download Sample Reports</span>
                <span className="nis2-button__arrow">↓</span>
              </a>
            </div>
          </div>
        </section>

        {/* Section 2: Why Open Source */}
        <section className="nis2-opensource">
          <div className="container">
            <h2 className="nis2-section__title">
              Why We Open Sourced nis2scan
            </h2>
            <div className="nis2-opensource__content">
              <p>Security tools should be transparent.</p>
              <p>
                If you are going to run a scanner against your infrastructure,
                you should be able to read exactly what it does. We built
                nis2scan to solve our own NIS2 compliance problem, and we open
                sourced it because we believe the security community benefits
                from tools you can inspect, audit, and trust.
              </p>
              <p>
                The source code is available on GitHub. You can compile it
                yourself, contribute to it, or report issues. Pre-built binaries
                with PDF report generation and installation support are
                available for purchase — because most companies would rather pay
                once than spend days setting up a Rust toolchain.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: What It Checks */}
        <section className="nis2-controls">
          <div className="container">
            <h2 className="nis2-section__title">9 NIS2 Article 21 Controls</h2>
            <div className="nis2-controls__grid">
              {controls.map((control) => (
                <div key={control.num} className="nis2-control-card">
                  <div className="nis2-control-card__icon">{control.icon}</div>
                  <div className="nis2-control-card__content">
                    <h3 className="nis2-control-card__title">
                      {control.num}. {control.name}
                    </h3>
                    <p className="nis2-control-card__article">
                      Article {control.article}
                    </p>
                    <p className="nis2-control-card__desc">{control.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 4: Before and After */}
        <section className="nis2-results">
          <div className="container">
            <h2 className="nis2-section__title">Real Results</h2>
            <p className="nis2-section__subtitle">
              Same server. 30 minutes of fixes.
            </p>

            <div className="nis2-results__grid">
              <div className="nis2-result-card nis2-result-card--before">
                <h3 className="nis2-result-card__header">BEFORE</h3>
                <div className="nis2-result-card__score">
                  Score: 34/100 — CRITICAL ISSUES
                </div>
                <ul className="nis2-result-card__findings">
                  {beforeFindings.map((finding, i) => (
                    <li
                      key={i}
                      className={`nis2-finding nis2-finding--${finding.status}`}
                    >
                      {finding.status === "fail" && "✗ "}
                      {finding.status === "warning" && "⚠ "}
                      {finding.status === "pass" && "✓ "}
                      {finding.text}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="nis2-result-card nis2-result-card--after">
                <h3 className="nis2-result-card__header">AFTER</h3>
                <div className="nis2-result-card__score">
                  Score: 77/100 — MINOR ISSUES
                </div>
                <ul className="nis2-result-card__findings">
                  {afterFindings.map((finding, i) => (
                    <li
                      key={i}
                      className={`nis2-finding nis2-finding--${finding.status}`}
                    >
                      {finding.status === "fail" && "✗ "}
                      {finding.status === "warning" && "⚠ "}
                      {finding.status === "pass" && "✓ "}
                      {finding.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="nis2-results__highlight">
              <p className="nis2-results__score-jump">
                Score jumped 34 → 77 in one session
              </p>
              <p className="nis2-results__note">
                Every finding includes the exact terminal command to fix it.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5: Sample Reports */}
        <section id="sample-reports" className="nis2-samples">
          <div className="container">
            <h2 className="nis2-section__title">Download Sample Reports</h2>
            <p className="nis2-section__subtitle">
              See exactly what an auditor receives — tested on both Windows and
              Linux.
            </p>

            <div className="nis2-samples__grid">
              {product.sampleReports.map((report, i) => (
                <div key={i} className="nis2-sample-card">
                  <div className="nis2-sample-card__icon">
                    {i === 0 ? "🪟" : "🐧"}
                  </div>
                  <h3 className="nis2-sample-card__title">{report.label}</h3>
                  <p className="nis2-sample-card__desc">
                    {i === 0
                      ? "Complete NIS2 compliance scan on Windows Server with detailed findings and remediation commands"
                      : "Full infrastructure assessment on Linux with auditor-ready PDF output"}
                  </p>
                  <a
                    href={report.path}
                    download={report.filename}
                    className="nis2-button nis2-button--outline"
                  >
                    Download PDF
                  </a>
                </div>
              ))}
            </div>

            <p className="nis2-samples__note">
              These are real reports generated by nis2scan on live servers.
              Company name changed for privacy.
            </p>
          </div>
        </section>

        {/* Section 6: Pricing */}
        <section className="nis2-pricing">
          <div className="container">
            <h2 className="nis2-section__title">Pricing</h2>
            <p className="nis2-section__subtitle">
              One time purchase. You own it permanently.
            </p>

            <div className="nis2-pricing__grid">
              {product.pricing.map((tier, i) => (
                <div
                  key={i}
                  className={`nis2-pricing-card ${tier.popular ? "nis2-pricing-card--popular" : ""}`}
                >
                  {tier.popular && (
                    <div className="nis2-pricing-card__badge">Most Popular</div>
                  )}
                  <h3 className="nis2-pricing-card__tier">{tier.tier}</h3>
                  <div className="nis2-pricing-card__price">
                    €{tier.price.toLocaleString()}
                  </div>
                  <p className="nis2-pricing-card__desc">{tier.description}</p>
                  <a
                    href={`mailto:axiomlabssolutions@gmail.com?subject=${encodeURIComponent(`License inquiry — ${tier.tier}`)}`}
                    className="nis2-button nis2-button--primary"
                  >
                    Contact Sales
                  </a>
                </div>
              ))}
            </div>

            <p className="nis2-pricing__note">
              Or build it yourself — source code is free on GitHub. Pre-built
              binaries include PDF generation and installation support.
            </p>

            <div className="nis2-pricing__github">
              <a
                href={product.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="nis2-button nis2-button--outline"
              >
                View Source on GitHub →
              </a>
            </div>
          </div>
        </section>

        {/* Section 7: Open Source Callout */}
        <section className="nis2-callout">
          <div className="container">
            <h2 className="nis2-callout__title">Source Available on GitHub</h2>
            <p className="nis2-callout__text">
              Read the code, build it yourself, or contribute. Commercial binary
              licenses available at the pricing above.
            </p>
            <div className="nis2-callout__buttons">
              <a
                href={product.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="nis2-button nis2-button--white"
              >
                View on GitHub →
              </a>
              <a
                href={`${product.githubUrl}/blob/main/LICENSE`}
                target="_blank"
                rel="noopener noreferrer"
                className="nis2-button nis2-button--white-outline"
              >
                Read the License →
              </a>
            </div>
          </div>
        </section>

        {/* Section 8: Technical Specs */}
        <section className="nis2-specs">
          <div className="container">
            <h2 className="nis2-section__title">Technical Specifications</h2>
            <div className="nis2-specs__grid">
              <div className="nis2-spec">
                <span className="nis2-spec__label">Language</span>
                <span className="nis2-spec__value">Rust</span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Binary size</span>
                <span className="nis2-spec__value">
                  ~8MB Linux / ~5MB Windows
                </span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Scan time</span>
                <span className="nis2-spec__value">3-7 seconds per host</span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Platforms</span>
                <span className="nis2-spec__value">
                  Linux (x86_64) · Windows (x86_64)
                </span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Dependencies</span>
                <span className="nis2-spec__value">
                  Zero (statically linked)
                </span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Output</span>
                <span className="nis2-spec__value">PDF · JSON</span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Network access</span>
                <span className="nis2-spec__value">
                  SSH (Linux) · WinRM (Windows)
                </span>
              </div>
              <div className="nis2-spec">
                <span className="nis2-spec__label">Architecture</span>
                <span className="nis2-spec__value">
                  All 9 controls run in parallel
                </span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default ProductNis2scan;
